/* ============================================================
   <product-card-gallery>
   Lightweight carousel for product cards.
   - Lazy-loads slides 2..N on first user interaction.
   - Prev/next buttons use native scroll-snap via scrollIntoView.
   - Progress bar driven by a single IntersectionObserver per card.
   - Public API: rebuildSlides(mediaArray) for swatch changes.
   ============================================================ */

class ProductCardGallery extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.slides = [];
    this.prevBtn = null;
    this.nextBtn = null;
    this.io = null;
    this.lazyDone = false;
    this.currentIndex = 0;
    this._cloneFirst = null;
    this._cloneLast = null;
    this._scrollEndAttached = false;
    this._onPrev = (e) => { e.preventDefault(); this._go(-1); };
    this._onNext = (e) => { e.preventDefault(); this._go(+1); };
    this._lazyTrigger = () => this._loadAllSlides();
  }

  connectedCallback() {
    this.track   = this.querySelector('.pcg__track');
    this.slides  = Array.from(this.querySelectorAll('.pcg__slide'));
    this.prevBtn = this.querySelector('.pcg__arrow--prev');
    this.nextBtn = this.querySelector('.pcg__arrow--next');

    if (!this.track || this.slides.length <= 1) return;

    this._setProgress(0);

    this.addEventListener('pointerenter', this._lazyTrigger, { once: true, passive: true });
    this.addEventListener('touchstart',   this._lazyTrigger, { once: true, passive: true });

    if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);

    this._observeSlides();
    this._updateArrows();
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
  }

  _observeSlides() {
    if (this.io) this.io.disconnect();
    this.io = new IntersectionObserver(
      (entries) => this._onIntersect(entries),
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((s) => this.io.observe(s));
  }

  _loadAllSlides() {
    if (this.lazyDone) return;
    this.lazyDone = true;
    for (let i = 1; i < this.slides.length; i++) {
      const img = this.slides[i].querySelector('img');
      if (!img) continue;
      const src    = img.dataset.src;
      const srcset = img.dataset.srcset;
      if (src)    img.src    = src;
      if (srcset) img.srcset = srcset;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }
    this._setupClones();
  }

  _setupClones() {
    if (this.slides.length < 2 || this._cloneFirst) return;

    const last  = this.slides[this.slides.length - 1].cloneNode(true);
    const first = this.slides[0].cloneNode(true);

    last.setAttribute('data-clone', 'last');
    last.setAttribute('aria-hidden', 'true');
    first.setAttribute('data-clone', 'first');
    first.setAttribute('aria-hidden', 'true');

    this._cloneLast  = last;
    this._cloneFirst = first;

    const prevScroll = this.track.scrollLeft;
    this.track.insertBefore(last, this.slides[0]);
    this.track.appendChild(first);

    this._instantScroll(prevScroll + last.offsetWidth);
    this._setupScrollEndHandler();
  }

  _setupScrollEndHandler() {
    if (this._scrollEndAttached) return;
    this._scrollEndAttached = true;
    const handler = () => this._maybeTeleport();
    if ('onscrollend' in window) {
      this.track.addEventListener('scrollend', handler, { passive: true });
    } else {
      let t;
      this.track.addEventListener('scroll', () => {
        clearTimeout(t);
        t = setTimeout(handler, 120);
      }, { passive: true });
    }
  }

  _maybeTeleport() {
    if (!this._cloneFirst || !this._cloneLast) return;
    const sl = this.track.scrollLeft;
    const tolerance = 4;
    if (Math.abs(sl - this._cloneLast.offsetLeft) < tolerance) {
      this._instantScroll(this.slides[this.slides.length - 1].offsetLeft);
    } else if (Math.abs(sl - this._cloneFirst.offsetLeft) < tolerance) {
      this._instantScroll(this.slides[0].offsetLeft);
    }
  }

  _instantScroll(left) {
    const prev = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = left;
    requestAnimationFrame(() => {
      this.track.style.scrollBehavior = prev;
    });
  }

  _go(delta) {
    this._loadAllSlides();
    const n = this.slides.length;
    if (n === 0) return;

    let target;
    if (delta > 0 && this.currentIndex === n - 1 && this._cloneFirst) {
      target = this._cloneFirst;
    } else if (delta < 0 && this.currentIndex === 0 && this._cloneLast) {
      target = this._cloneLast;
    } else {
      const next = ((this.currentIndex + delta) % n + n) % n;
      target = this.slides[next];
    }
    target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }

  _onIntersect(entries) {
    let best = null;
    let ratio = 0;
    for (const e of entries) {
      if (e.intersectionRatio > ratio) {
        ratio = e.intersectionRatio;
        best  = e.target;
      }
    }
    if (!best) return;
    const idx = this.slides.indexOf(best);
    if (idx === -1 || idx === this.currentIndex) return;
    this.currentIndex = idx;
    this._setProgress(idx);
    this._updateArrows();
  }

  _setProgress(idx) {
    const n = this.slides.length;
    const progress = n > 1 ? idx / (n - 1) : 0;
    this.style.setProperty('--pcg-progress', progress);
  }

  _updateArrows() {
    if (this.prevBtn) this.prevBtn.hidden = false;
    if (this.nextBtn) this.nextBtn.hidden = false;
  }

  rebuildSlides(mediaArray) {
    if (!Array.isArray(mediaArray) || mediaArray.length === 0 || !this.track) return;
    if (this.io) this.io.disconnect();
    this.track.innerHTML = this._buildHTML(mediaArray);
    this.slides = Array.from(this.track.querySelectorAll('.pcg__slide'));
    this.lazyDone = false;
    this.currentIndex = 0;
    this._cloneFirst = null;
    this._cloneLast = null;
    this._setProgress(0);
    this.track.scrollLeft = 0;
    if (this.slides.length > 1) {
      this._observeSlides();
    }
    this._updateArrows();
  }

  _buildHTML(media) {
    const widths = [200, 300, 400, 500, 600, 700, 800, 1000, 1200];
    const sizes  = this.dataset.sizes || '';
    return media.map((m, i) => {
      const src = (m.preview_image && m.preview_image.src) || m.src || '';
      if (!src) return '';
      const srcset = widths.map((w) => `${src}?width=${w} ${w}w`).join(', ');
      const alt = String(m.alt || '').replace(/"/g, '&quot;');
      const w   = (m.preview_image && m.preview_image.width)  || m.width  || '';
      const h   = (m.preview_image && m.preview_image.height) || m.height || '';
      if (i === 0) {
        return (
          `<div class="pcg__slide h-full" data-index="0">` +
            `<img class="product-card__image w-full h-full object-cover is-active" ` +
                 `src="${src}" srcset="${srcset}" sizes="${sizes}" ` +
                 `width="${w}" height="${h}" alt="${alt}">` +
          `</div>`
        );
      }
      return (
        `<div class="pcg__slide h-full" data-index="${i}">` +
          `<img class="product-card__image w-full h-full object-cover" ` +
               `src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==" ` +
               `data-src="${src}" data-srcset="${srcset}" sizes="${sizes}" ` +
               `width="${w}" height="${h}" alt="${alt}" ` +
               `loading="lazy" fetchpriority="low">` +
          `</div>`
      );
    }).join('');
  }
}

if (!customElements.get('product-card-gallery')) {
  customElements.define('product-card-gallery', ProductCardGallery);
}
